import React, { useCallback, useContext, useMemo, useState, FC } from 'react';
import Style from './notes.module.scss';
import { ExpandedRowHeader } from '../header/expandedRowHeader.component';
import { AppButton } from 'uikit/buttons';
import { ReactComponent as AddIcon } from '../../../assets/img/common/add.svg';
import { Note } from './Note';
import { PatientCardContext } from '../PatientCardContext';
import { useFormEditing } from '../../../features/forms/editing/useFormEditing';
import { useScopedTranslation } from '../../../application/localisation/useScopedTranslation';
import { useCommonLocalization } from '../../../application/localisation/useCommonLocalization';
import clsx from 'clsx';
import { Loading } from 'uikit/suspense/Loading';
import { IssueMark } from 'src/components/issue/issueTarget/issueMark.component';
import { useNotesOverview } from './overview/useNotesOverview';
import { useHasAccessToForm, useHasStudyClaims } from 'src/helpers/auth/auth-helper';
import { StudyRoleClaimValue } from 'src/services/api/api-client';
import { useGetFormByIdQuery, useGetFormResultsQuery } from '../../../services/api/api-client/FormsQuery';
import { AppButtonWithTooltip } from 'uikit/buttons/buttonWithTooltip/buttonWithTooltip';

const NOTES_LOAD_LIMIT = 3;

export const NotesSection: FC<{ isColored?: boolean }> = (props: { isColored?: boolean }) => {
  const { t } = useScopedTranslation('Dashboard.PatientAccordion.NotesSection');
  const commonLocalizer = useCommonLocalization();

  const patientCardContext = useContext(PatientCardContext);
  const formConfigId = patientCardContext.group.formConfigIdForNotes;
  const noteDateDataKey = patientCardContext.group.noteDateDataKey;
  const isStudyFinished = patientCardContext.patient.isStudyFinished;
  const studyRouteProgress = patientCardContext.patient.studyRouteProgress;
  const patientId = patientCardContext.patient.id;
  const patientUniqId = patientCardContext.patient.uniqueId;

  const [isEndReached, setEndReached] = useState<boolean>(false);
  const notesOverview = useNotesOverview();
  const { hasStudyClaim } = useHasStudyClaims();
  const { hasAccessToForm } = useHasAccessToForm();

  //#region Data fetching

  const formConfig = useGetFormByIdQuery(formConfigId!, {
    enabled: hasAccessToForm(formConfigId) && !!formConfigId,
    suspense: false,
  });

  const formResults = useGetFormResultsQuery(
    {
      studyId: patientCardContext.patient.studyId,
      patientId: patientId,
      formConfigId: formConfigId,
      orderByDataKey: noteDateDataKey,
      limit: NOTES_LOAD_LIMIT,
      lastFormResultVersionOnly: true,
    },
    {
      enabled: hasAccessToForm(formConfigId) && !!formConfigId,
      suspense: false,
    },
  );

  const isInitialLoading = formConfig.isInitialLoading || formResults.isInitialLoading;

  //#endregion

  //#region Editing

  const formEditing = useFormEditing();

  const [startingEditing, setStartingEditing] = useState(false);
  const onAddNote = useCallback(() => {
    if (startingEditing) return;
    setStartingEditing(true);
    formEditing
      .startFormFilling({ patientId, routeProgress: studyRouteProgress, formId: formConfigId! })
      .finally(() => setStartingEditing(false));
  }, [formConfigId, formEditing, patientId, startingEditing, studyRouteProgress]);

  //#endregion

  const noteVms = useMemo(() => {
    if (!formConfig?.data || !formResults?.data) return [];

    return formResults?.data?.data?.map((x) => ({
      form: formConfig.data,
      formResult: x,
    }));
  }, [formConfig.data, formResults?.data]);

  const onScroll = useCallback(async (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    setEndReached(scrollTop + clientHeight >= scrollHeight - 5);
  }, []);

  return (
    <>
      <div className={Style.notesSection} data-test-id={'notes-section'}>
        <ExpandedRowHeader
          text={
            <IssueMark
              issueContext={{
                subject: 'Patient',
                topic: 'Notes',
                linkedPatientUniqId: patientUniqId,
              }}
              countDescendants={false}
              containerClassName={Style.issueMarkContainer}
            >
              {t('Header')}
            </IssueMark>
          }
          rightHeader={
            !isStudyFinished &&
            hasStudyClaim(StudyRoleClaimValue.PatientCreate) &&
            hasAccessToForm(formConfigId) && (
              <AppButtonWithTooltip
                onClick={onAddNote}
                variant={'icon-link'}
                colorSchema={'primary'}
                Icon={AddIcon}
                tooltip={t('AddNote')}
              />
            )
          }
          testId={'notes-header'}
        />

        <div className={Style.notesSectionBody} onScroll={onScroll}>
          <Loading loading={isInitialLoading} containerClassName={Style.loading} testId={'notes-section'}>
            {noteVms.map(
              (x, index, array) =>
                patientCardContext.group.noteDateDataKey &&
                x.formResult && (
                  <React.Fragment key={x.formResult.id}>
                    <Note
                      testId={'note'}
                      formDto={x.form}
                      formResult={x.formResult}
                      patientUniqueId={patientCardContext.patient.uniqueId}
                      noteDateDataKey={patientCardContext.group.noteDateDataKey}
                      canEdit={
                        hasStudyClaim(StudyRoleClaimValue.PatientCreate) && !patientCardContext.patient?.isStudyFinished
                      }
                    />
                    {index + 1 < array.length && <hr className={Style.notesBreak} />}
                  </React.Fragment>
                ),
            )}
          </Loading>
        </div>
        {formResults.data &&
          formResults.data?.totalCount > NOTES_LOAD_LIMIT &&
          !isInitialLoading &&
          formConfigId &&
          patientCardContext.patient.id && (
            <div
              className={clsx(Style.seeAllFooter, {
                [Style.seeAllFooterBlur]: !isEndReached,
                [Style.seeAllFooterBlurColored]: props.isColored,
              })}
            >
              <AppButton
                onClick={() =>
                  notesOverview.open({
                    configId: formConfigId,
                    patientId: patientCardContext.patient.id,
                  })
                }
                variant={'icon-link'}
                colorSchema={'primary'}
                text={commonLocalizer('Common_SeeAll')}
                className={Style.seeAllBtn}
                testId={'see-all'}
              />
            </div>
          )}
      </div>

      {formEditing.element}
      {notesOverview.element}
    </>
  );
};
